import type { FormGroup, UntypedFormControl } from '@angular/forms';
import { type Observable } from 'rxjs';
import { DirectoryItemCategory, FmSourceType, type SelectedFileInterface } from '@sendpulse/file-manager';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { BeforeAfterWidgetData } from '@web/widgets/before-after/type/before-after-widget.type';
import { EHandleOrientation } from '@web/widgets/before-after/type/before-after-widget.type';

import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { extendWebFileDataPromise } from '@common/helpers';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { WidgetsDataTypes } from '@common/enums';
import { environment } from '@web-env/environment';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { PreviewType, type ReplacedFile } from '@components/sp-file-manager/src/types';
import { CommonModule } from '@angular/common';
import { SpFileManagerComponentModule } from '@components/sp-file-manager';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpSliderComponent } from '@ui/sp-slider/src/sp-slider.component';

@Component({
    selector: 'web-before-after-control',
    templateUrl: './before-after-control.component.html',
    styleUrls: ['./before-after-control.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        SpFileManagerComponentModule,
        CommonElementsModule,
        WebColorPickerComponent,
        TooltipModule,
        SpSliderComponent,
    ],
})
export class BeforeAfterControlComponent extends WebBaseControlComponent<BeforeAfterWidgetData> {
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly useUnsplash: UseUnsplash = inject(UseUnsplash);

    public readonly PreviewType = PreviewType;
    public readonly DirectoryItemCategory = DirectoryItemCategory;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EHandleOrientation = EHandleOrientation;

    public editableBeforeImage = null;
    public editableAfterImage = null;
    public buttonDisabled: boolean = false;

    public columnWidth: number;

    public readonly fmSourceType = FmSourceType;
    public readonly userId$: Observable<number> = this.userFacade.userId$;

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: this.fb.group({
                beforeImage: null,
                afterImage: null,
                horizontalAlign: null,
                mobileHorizontalAlign: null,
                isSynchronizeStyle: null,
                handleOrientation: null,
                handleColor: null,
                handlePosition: null,
                maxWidth: null,
            }),
        });
    }

    protected setFormData(): void {
        const data = { ...this.widgetData };

        this.controlForm.patchValue(
            {
                data,
            },
            { emitEvent: false }
        );

        // TODO: TMP KOSTIL REFACTOR
        this.columnWidth = 0;
        if (!this.isSpecific) {
            this.columnWidth = (
                document.querySelector(`[id='${this.widget.columnId}'] .img-widget-width`) as HTMLDivElement
            )?.offsetWidth;
        } else {
            this.columnWidth = document.getElementById(this.widget.columnId)?.offsetWidth;
        }

        if (!this.maxWidth.value) {
            this.maxWidth.setValue(this.columnWidth, { emitEvent: false });
        }

        if (this.columnWidth && this.columnWidth < this.maxWidth.value) {
            this.maxWidth.setValue(this.columnWidth, { emitEvent: false });
        }

        if (data.beforeImage && data?.beforeImage?.isTargetBlank === undefined) {
            this.beforeImage.patchValue({ ...this.beforeImage.value, isTargetBlank: false });
        }

        if (data.afterImage && data?.afterImage?.isTargetBlank === undefined) {
            this.afterImage.patchValue({ ...this.afterImage.value, isTargetBlank: false });
        }
    }

    public setImageData(imageData: SelectedFileExtended): void {
        if (this.editableBeforeImage) {
            this.updateWidgetData({ beforeImage: { ...this.beforeImage.value, ...imageData } });
            this.editableBeforeImage = null;
        } else if (this.editableAfterImage) {
            this.updateWidgetData({ afterImage: { ...this.afterImage.value, ...imageData } });
            this.editableAfterImage = null;
        }
    }

    public async setBeforeImage(file: SelectedFileInterface): Promise<void> {
        this.buttonDisabled = true;
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);

        this.beforeImage.setValue(extendedFile, { emitEvent: false });
        this.maxWidth.setValue(extendedFile.imageWidth);

        this.maxWidth.setValue(this.columnWidth);

        this.siteFacade.resizeImage({
            path: extendedFile.path,
            url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
        });

        this.buttonDisabled = false;
    }

    public async setAfterImage(file: SelectedFileInterface): Promise<void> {
        this.buttonDisabled = true;
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);

        this.afterImage.setValue(extendedFile, { emitEvent: false });
        this.maxWidth.setValue(extendedFile.imageWidth);

        this.maxWidth.setValue(this.columnWidth);

        this.siteFacade.resizeImage({
            path: extendedFile.path,
            url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
        });

        this.buttonDisabled = false;
    }

    public async replaceBeforeImage({ file }: ReplacedFile): Promise<void> {
        this.buttonDisabled = true;
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.beforeImage.setValue(extendedFile, { emitEvent: false });
        this.maxWidth.setValue(extendedFile.imageWidth);

        this.maxWidth.setValue(this.columnWidth);

        this.siteFacade.resizeImage({
            path: extendedFile.path,
            url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
        });

        this.buttonDisabled = false;
    }

    public async replaceAfterImage({ file }: ReplacedFile): Promise<void> {
        this.buttonDisabled = true;
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.afterImage.setValue(extendedFile, { emitEvent: false });
        this.maxWidth.setValue(extendedFile.imageWidth);

        this.maxWidth.setValue(this.columnWidth);

        this.siteFacade.resizeImage({
            path: extendedFile.path,
            url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
        });

        this.buttonDisabled = false;
    }

    openUnsplashModal(imageType: 'before' | 'after'): void {
        this.useUnsplash.openUnsplashModal({
            widgetId: this.widget.id,
            dataType: imageType === 'before' ? WidgetsDataTypes.beforeImage : WidgetsDataTypes.afterImage,
        });
    }

    public removeBeforeFile(): void {
        this.beforeImage.setValue(null);
    }

    public removeAfterFile(): void {
        this.afterImage.setValue(null);
    }

    public setHorizontalAlign(value: string): void {
        this.horizontalAlign.setValue(value);
    }

    public setMobileHorizontalAlign(value: string): void {
        this.mobileHorizontalAlign.setValue(value);
    }

    public setMaxWidth(value: number): void {
        this.maxWidth.setValue(value);
    }

    public setHandleOrientation(value: EHandleOrientation): void {
        this.handleOrientation.setValue(value);
    }

    public setHandleColor(event: ColorPickerEventPayload): void {
        this.handleColor.setValue(event.value);
    }

    public setHandlePosition(value: number): void {
        this.handlePosition.setValue(value);
    }

    public get beforeImage(): UntypedFormControl {
        return this.data.get('beforeImage') as UntypedFormControl;
    }

    public get afterImage(): UntypedFormControl {
        return this.data.get('afterImage') as UntypedFormControl;
    }

    public get horizontalAlign(): UntypedFormControl {
        return this.data.get('horizontalAlign') as UntypedFormControl;
    }

    public get mobileHorizontalAlign(): UntypedFormControl {
        return this.data.get('mobileHorizontalAlign') as UntypedFormControl;
    }

    public get handleOrientation(): UntypedFormControl {
        return this.data.get('handleOrientation') as UntypedFormControl;
    }

    public get handleColor(): UntypedFormControl {
        return this.data.get('handleColor') as UntypedFormControl;
    }

    public get handlePosition(): UntypedFormControl {
        return this.data.get('handlePosition') as UntypedFormControl;
    }

    public get maxWidth(): UntypedFormControl {
        return this.data.get('maxWidth') as UntypedFormControl;
    }

    public refreshImageWidth(): void {
        this.maxWidth.setValue(this.columnWidth);
    }

    public showEditBeforeImageModal() {
        this.showImageEditor(this.beforeImage.value, WidgetsDataTypes.beforeImage);
    }

    public showEditAfterImageModal() {
        this.showImageEditor(this.afterImage.value, WidgetsDataTypes.afterImage);
    }

    public get beforeImageOwnerUserId(): number {
        if (!this.beforeImage.value?.url) return null;
        const urlParts = this.beforeImage.value.url.split('/');
        return Number(urlParts[1].substring(32));
    }

    public get afterImageOwnerUserId(): number {
        if (!this.afterImage.value?.url) return null;
        const urlParts = this.afterImage.value.url.split('/');
        return Number(urlParts[1].substring(32));
    }

    public get position(): any {
        return this.data.get('position') as FormGroup<any>;
    }
}
